// Must be updated whenever version is updated
const VERSION = '2.6.3';
const { uaCall, getRealUA, uaCallWithReturnValue } = require('./ua');
const {
    logError,
    logWarn,
    delegate,
    getCategory,
    getAction,
    getLabel,
    getValue,
    getNoninteraction,
    getHref,
    getDimensions,
    getTarget,
    setGAIntegrationIfNotExists,
} = require('./utils');
const datalayer = require('./datalayer');

let globalClickListenerInitialized = false;
let googleAnalyticsEnabled = true;

/**
 * Send a tracking event.
 *
 * @deprecated since 2.1.0. Use {@link track} instead.
 * @param {Object}   properties
 * @param {String}   properties.category Event category
 * @param {String}   properties.action Event action
 * @param {String}   [properties.label] Event label
 * @param {Number}   [properties.value] Event value
 * @param {Boolean}  [properties.noninteraction] Noninteraction event
 * @param {String}   [properties.href] Redirect to URL after event fires
 * @param {String}   [properties.target] The URL target, default: '_self'
 * @param {Function} [properties.hitCallback] A callback function
 */
function trackEvent(properties) {
    properties.uaMethodName = 'UA-trackEvent';
    properties.uaVersion = VERSION;
    uaCall('trackEvent', properties);
}

/**
 * Send a tracking event.
 *
 * trackEventV2 is partially backward compatible with @see trackEvent
 * V2 designed to pass more optional data to zanalytics
 * breaking change: hitCallback moved from the first function parameter
 * (properties) to the second (options).
 *
 * @deprecated since 2.1.0. Use {@link track} instead.
 * @param {Object}   properties
 * @param {String}   properties.category Event category
 * @param {String}   properties.action Event action
 * @param {String}   [properties.label] Event label
 * @param {Number}   [properties.value] Event value
 * @param {Boolean}  [properties.noninteraction] Noninteraction event
 * @param {IOverrideOptions} [options]
 */
function trackEventV2(properties, options) {
    options = options || {};
    options.uaMethodName = 'UA-trackEventV2';
    options.uaVersion = VERSION;
    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('trackEventV2', properties, options);
}

/**
 * Send a pageview event.
 *
 * @deprecated since 2.1.0. Use {@link page} instead.
 * @param {Object} properties
 * @param {String} properties.pageURL The page URL
 */
function trackPageview(properties) {
    properties.uaMethodName = 'UA-trackPageView';
    properties.uaVersion = VERSION;
    uaCall('trackPageview', properties);
}

/**
 * Send a pageview event.
 *
 * trackPageviewV2 is backward compatible with @see trackPageview
 * V2 designed to pass more optional data to zanalytics
 *
 * @deprecated since 2.1.0. Use {@link page} instead.
 * @param {Object} properties
 * @param {string} properties.pageURL The page URL
 * @param {IOverrideOptions} [options]
 */
function trackPageviewV2(properties, options) {
    options = options || {};
    options.uaMethodName = 'UA-trackPageViewV2';
    options.uaVersion = VERSION;
    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('trackPageviewV2', properties, options);
}

/**
 * Identifies logged in user with userId.
 *
 * @deprecated since 2.1.0. Use {@link identify} instead.
 * @param {Object} config
 * @param {String} config.userId
 */
function identifyUser(config) {
    uaCall('identifyUser', config);
}

/**
 * Set custom dimensions.
 *
 * @deprecated since 2.1.0. Use {@link setdim} instead.
 * @param {object} dimensions Keyed by dimension name
 */
function setCustomDimensions(dimensions) {
    uaCall('setCustomDimensions', dimensions);
}

/**
 * Get the global Google Analytics object.
 *
 * @deprecated since 2.1.0.
 * @returns {Object} The Google Analytics global object.
 */
function getGA() {
    return typeof window !== 'undefined' && window.GoogleAnalyticsObject
        ? window[window.GoogleAnalyticsObject]
        : undefined;
}

/**
 * Applies any passed arguments to the ga function.
 *
 * @deprecated since 2.1.0.
 * @param {...any} args
 */
function gaCommand(...args) {
    const ga = getGA();

    if (typeof ga !== 'undefined') {
        ga.apply(window, args);
    } else {
        logWarn('gaCommand called, but GA was not initialized!');
    }
}

/**
 * Initialize the global click listener. The listener will handle all
 * clicks on elements with the class `za-track-event`.
 *
 * @deprecated since 2.1.2.
 */
function initGlobalClickListener() {
    if (typeof document !== 'undefined' && !globalClickListenerInitialized) {
        delegate(document.body, 'click', handleTrackEventClick, '.za-track-event');
        globalClickListenerInitialized = true;
    }
}

/**
 * In most cases, this function will be called by the global click
 * listener set up by `initGlobalClickListener`.
 * The function can also be called itself in the case
 * where additional logic is required during the click event.
 *
 * The basic idea is to wrap `trackEvent`
 * functionality such that you can track events directly
 * from the DOM. Clicking on an element with class `za-track-event` will
 * trigger the event. You can define `trackEvent`
 * parameters using data attributes on the element, e.g.
 * `data-za-category`, `data-za-action`.
 *
 * Data attributes values are generally defined on the element with the
 * `za-track-event` class, but they can also be configured to inherit
 * the value by using the `!inherit` keyword. When inherit is enabled
 * for an attribute, the value will be pulled from the nearest ancestor
 * that has the same data attribute defined.
 *
 * When the `href` parameter is defined, the URL `target` parameter will
 * be taken directly from the link when applicable or pulled from the
 * data attribute `data-za-target`. In the case that the element was
 * control or middle clicked, the target will automatically be set to
 * `_blank` (middle click will only work for anchor elements).
 *
 * ### HTML Example
 *
 *     <!-- Track event before redirecting. -->
 *     <a href="/mortgage-rates/"
 *         class="za-track-event"
 *         data-za-category="Mortgages"
 *         data-za-action="Rate link click"
 *     >Mortgage rates</a>
 *
 *     <!-- Does not have to be an anchor! -->
 *     <div data-za-href="/mortgage-rates/"
 *         class="za-track-event"
 *         data-za-category="Mortgages"
 *         data-za-action="Rate link click"
 *     >Mortgage rates</div>
 *
 *     <!-- Inherit attributes from ancestors. -->
 *     <div data-za-category="Mortgages">
 *         <header data-za-label="Header">
 *             <a href="/mortgage-rates/"
 *                 class="za-track-event"
 *                 data-za-category="!inherit"
 *                 data-za-action="Rate link click"
 *                 data-za-label="!inherit"
 *             >Mortgage rates</a>
 *         </header>
 *         <footer data-za-label="Footer">
 *             <a href="/mortgage-rates/"
 *                 class="za-track-event"
 *                 data-za-category="!inherit"
 *                 data-za-action="Rate link click"
 *                 data-za-label="!inherit"
 *             >Mortgage rates</a>
 *         </footer>
 *     </div>
 *
 * @deprecated since 2.1.2.
 * @method handleTrackEventClick
 * @param {Event} e The click event
 * @param {Node} target The node that was clicked
 */
function handleTrackEventClick(e, target) {
    const evt = {
        category: getCategory(target),
        action: getAction(target),
        label: getLabel(target),
        value: getValue(target),
        noninteraction: getNoninteraction(target),
        href: getHref(target),
        dimensions: getDimensions(target),
        target: getTarget(e, target),
    };

    if (typeof evt.href !== 'undefined' && evt.href !== null) {
        e.preventDefault();
    }

    trackEvent(evt);
}

/**
 * Load the Google Analytics SDK
 *
 * @param {String} [globalName] The global GA object name.
 * @param {String} [cdnUrl] URL from which to download the Google Analytics SDK
 */
/* istanbul ignore next */
function requestUAScript(globalName, cdnUrl) {
    const gaGlobalName = globalName || 'ga';
    const templateCdnUrl = cdnUrl || '//www.google-analytics.com/analytics.js';

    try {
        /* eslint-disable */
        // prettier-ignore
        (function (i, s, o, g, r, a, m) {
            i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function () {
                (i[r].q = i[r].q || []).push(arguments);
            }; i[r].l = 1 * new Date(); a = s.createElement(o);
            m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m);
        })(window, document, 'script', templateCdnUrl, gaGlobalName);
        /* eslint-enable */
    } catch (error) {
        logError('Tried to invoke GA loading script, but there was an error.', error);
    }
}

/**
 * Send a track event
 *
 * @param {Object}   properties track properties
 * @param {String}   [properties.category] Event category
 * @param {String}   [properties.action] Event action
 * @param {String}   [properties.label] Event label
 * @param {Number}   [properties.value] Event value
 * @param {Boolean}  [properties.noninteraction] Noninteraction event
 * @param {IOverrideOptions} [options] optional options parameter
 * @param {String}   [eventName] event name, defaults to "event"
 */
function track(properties, options, eventName) {
    options = options || {};
    options.uaMethodName = 'UA-track';
    options.uaVersion = VERSION;
    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('track', properties, options, eventName);
}

/**
 * Send a pageview event.
 *
 * @param {String} path The path portion of a URL.
 *                      This value should start with a slash (/) character.
 * @param {Object} [properties] optional properties such as dimensions and metrics
 * @param {IOverrideOptions} [options] optional options parameter
 */
function page(path, properties, options) {
    properties = properties || {};
    options = options || {};
    options.uaMethodName = 'UA-page';
    options.uaVersion = VERSION;
    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('page', path, properties, options);
}

/**
 * Send an event to Clickstream
 *
 * Note: This function also supports legacy functionality that was deprecated
 *       on 2021-11-12. See README.md for deprecated functionality.
 *
 * @param {Object} properties event properties
 * @param {IOverrideOptions} [options] optional options parameter
 */
function event(properties, options) {
    options = options || {};
    options.uaMethodName = 'UA-event';
    options.uaVersion = VERSION;
    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('event', properties, options);
}

/**
 * Identify a logged-in user with userId
 * Note: userId will be set for Clickstream DSP only, not Google Analytics
 *
 * @param {string} userId logged-in userId
 */
function identify(userId) {
    uaCall('identify', userId);
}

/**
 * Manually set CDT data
 *
 * @param {Object} cdtData cross-domain-tracking data
 */
function setCDT(cdtData) {
    uaCall('setCDT', cdtData);
}

/**
 * Sets global custom dimensions. The dimensions will be effective up until next
 * page reload and will be set as a part of each next payload. New dimensions
 * will be merged to the previous ones.
 *
 * @param {Object} dimensions custom dimensions and/or metrics
 * @param {IOverrideOptions} [options] optional options parameter
 */
function setdim(dimensions, options) {
    options = options || {};

    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('setdim', dimensions, options);
}

/**
 * Set key=>value pairs that will be sent to Clickstream DSP
 * Note: properties set via this function will not be sent to Google Analytics
 *
 * @param {Object} properties key => value pairs
 * @param {IOverrideOptions} [options] optional options parameter
 */
function setprop(properties, options) {
    options = options || {};

    setGAIntegrationIfNotExists(googleAnalyticsEnabled, options);
    uaCall('setprop', properties, options);
}

/**
 * Starts a dwell stopwatch that is tracked by the label.
 * Dwell time will be reported automatically to Clickstream DSP when the user
 * closes or refreshes the web page or manually with {@link dwellEnd}.
 *
 * @param {string} label a string value describing the dwell stopwatch
 * @param {IDwellReporterConfig} config an object containing required dwell stopwatch options
 * @param {string} config.pageType a string value describing the type of page
 * @param {Object} [config.data] optional key=>value pairs to be passed along to Clickstream DSP
 */
function dwell(label, config) {
    uaCall('dwell', label, config);
}

/**
 * Manually stop a dwell stopwatch and report the data to Clickstream DSP.
 * This must be called on an existing dwell stopwatch after calling {@link dwell}
 *
 * @param {string} label a string value describing the dwell stopwatch
 */
function dwellEnd(label) {
    uaCall('dwellEnd', label);
}

/**
 * This is a low-level method which sends custom JSON payloads to a custom
 * Keystone HTTP endpoint.
 *
 * IMPORTANT: DO NOT use this method to send track or page events.
 * Use {@link track} or {@link page} instead.
 *
 * Note: This method does NOT distribute the payload to Google Analytics NOR Clickstream DSP.
 * Note: This method does not modify the source payload and does not add any fields to
 * the original payload.
 *
 * @param {string} guid a valid routing webhook UUID
 * @param {Object} payload any valid JS object (JSON payload)
 * @param {IOverrideOptions} [options] optional options parameter
 */
function send(guid, payload, options) {
    options = options || {};

    uaCall('send', guid, payload, options);
}

/**
 *
 * @param {Object} payload new lane payload
 */
function setPayloadDefaults(payload) {
    uaCall('setPayloadDefaults', payload);
}

/**
 * For auth0 flow only! Do not use for anything else.
 *
 * @returns {Promise<{key:string, value:string}>}
 * Resolves to object containing auth0 query params key and value
 *
 */
function getAuth0QueryParams() {
    return uaCallWithReturnValue('getAuth0QueryParams');
}

/**
 * For auth0 flow only! Do not use for anything else.
 *
 * @param {string} url auth0 url provided by producer
 * @returns {Promise<string>} Resolves to url with auth0 query params
 */
function getEnrichedAuth0Url(url) {
    return uaCallWithReturnValue('getEnrichedAuth0Url', url);
}

/**
 * Load and create Zillow Analytics
 *
 * @param {Window} window DOM window object
 * @param {Document} document DOM document object
 * @param {IPluginOptionsCollection} options (strictly required) an object
 *                                   that contains options used to determine
 *                                   where Zillow Analytics will send data
 * @param {string} [globalName] the globalName set to window.ZillowAnalyticsObject
 *                              defaults to "zanalytics"
 * @param {string} [cdnUrl] url from which to download the Zillow Analytics library
 */
function initZanalytics(window, document, options, globalName, cdnUrl) {
    const templateOptions = options || {
        zillow: {
            apiKey: 'undefinedKey',
            apiHost: 'e.zg-api.com',
            secure: true,
            anonymousId: '',
        },
    };
    const templateGlobalName = globalName || 'zanalytics';
    const templateCdnUrl = cdnUrl || 'https://e.zg-api.com/a/z/js/v1/analytics.js';

    templateOptions.uaVersion = VERSION;

    // prettier-ignore
    /* eslint-disable */
    function zaload(w,d,ns,cdn,opt,ml,fa,my,fst) {
        w.ZillowAnalyticsObject=ns;w[ns]=w[ns]||[];
        if(w[ns].initialize||w[ns].invoked) { return };
        w[ns].invoked=1;w[ns]._loadOptions=opt;
        ml=["identify","track","page","off","on","use","unuse","setdim","setprop","event","send","dwell","dwellEnd","setPayloadDefaults","getEnrichedAuth0Url","getAuth0QueryParams"];
        fa=function(m){return function(){w[ns].push([].concat(m,[].slice.call(arguments)));return w[ns]}};
        ml.forEach((mn) => {w[ns][mn]=fa(mn)});
        my=d.createElement("script");fst=d.getElementsByTagName("script")[0];
        my.async=!0;my.src=cdn;if(fst){fst.parentNode.insertBefore(my,fst)}else{try{d.head.appendChild(my);}catch(e){logError('Zillow Analytics failed to initialize', e)}};
        (function(w,n){w[n]=w[n]||function(){(w[n].q=w[n].q||[]).push(arguments)}})(w, 'ua');
    }
    zaload(window, document, templateGlobalName, templateCdnUrl, templateOptions);
    /* eslint-enable */
}

/**
 * Enables Google Analytics for all subsequent calls
 * Google Analytics is enabled by default.
 *
 * Each call can be individually controlled via the options parameter
 * using options.integrations.GaPlugin.
 */
function enableGA() {
    googleAnalyticsEnabled = true;
}

/**
 * Disables Google Analytics for all subsequent calls
 * Google Analytics is enabled by default.
 *
 * Each call can be individually controlled via the options parameter
 * using options.integrations.GaPlugin.
 */
function disableGA() {
    googleAnalyticsEnabled = false;
}

/**
 * Get the global setting of Google Analytics for all subsequent calls
 * Google Analytics is enabled by default.
 *
 * @returns {boolean} returns true if Google Analytics is enabled, false otherwise
 */
function isGAEnabled() {
    return googleAnalyticsEnabled;
}

/**
 * Test to see if the Universal Analytics component of Zillow Analytics is initialized.
 *
 * @returns {Boolean} true if Universal Analytics component of Zillow Analytics is initialized
 */
function isInitialized() {
    return typeof getRealUA() !== 'undefined';
}

module.exports = {
    trackEvent,
    trackEventV2,
    track,
    event,
    trackPageview,
    trackPageviewV2,
    page,
    identifyUser,
    identify,
    setCustomDimensions,
    setdim,
    setprop,
    dwell,
    dwellEnd,
    send,
    requestUAScript,
    initZanalytics,
    initGlobalClickListener,
    handleTrackEventClick,
    getGA,
    isInitialized,
    gaCommand,
    enableGA,
    disableGA,
    isGAEnabled,
    datalayer,
    setCDT,
    setPayloadDefaults,
    getAuth0QueryParams,
    getEnrichedAuth0Url,
};
